import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-uikit/Typography";
import { AuthenticatingSection } from "../AuthenticatingSection";
import { EmailDisplayBox } from "../EmailDisplayBox";
import { isExistingMember, isNewMember } from "../helpers";
import { useEmailVerificationOTPState } from "../hooks/useEmailVerificationOTPState";
import type { EmailVerificationStatus } from "../types";
import { FooterSection } from "./FooterSection";
import { OTPInput } from "./OTPInput";
import { ResendCodeSection } from "./ResendCodeSection";

const LOCALE_ROOT = "paywall_checkout.form.email_verification_states.otp_state";

interface EmailVerificationOTPStateProps {
  email: string;
  error?: string | null;
  isResending?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onOTPSubmit?: (otp: string) => void;
  onResendCode?: () => void;
  onShowClaimForm?: () => void;
  onShowPasswordForm: () => void;
  status: EmailVerificationStatus | null;
}

export const EmailVerificationOTPState = ({
  email,
  status,
  onClose,
  onShowPasswordForm,
  onShowClaimForm,
  onOTPSubmit,
  onResendCode,
  isSubmitting = false,
  isResending = false,
  error = null,
}: EmailVerificationOTPStateProps) => {
  const {
    hasError,
    shouldShowSuccess,
    shouldShowFooter,
    handleOTPComplete,
    handleResendClick,
  } = useEmailVerificationOTPState({
    status,
    isSubmitting,
    error,
    onOTPSubmit,
    onResendCode,
    onShowClaimForm,
  });

  return (
    <div className="flex flex-col gap-5 pb-6 *:px-6">
      <EmailDisplayBox email={email} onClose={onClose} />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <Typography.TitleSm weight="bold" size="text-base">
            {t([LOCALE_ROOT, "enter_verification_code"])}
          </Typography.TitleSm>
          <Typography.BodySm
            weight="normal"
            color="text-default"
            textAlign="flex flex-col gap-2"
          >
            <div>
              {t([LOCALE_ROOT, "enter_the_code_sent_to"])}&nbsp;
              <Typography.LabelSm weight="medium">{email}</Typography.LabelSm>
              &nbsp;{t([LOCALE_ROOT, "to_securely_authenticate_your_account"])}
            </div>
            <ResendCodeSection
              isSubmitting={isSubmitting}
              isResending={isResending}
              onResendClick={handleResendClick}
            />
          </Typography.BodySm>
        </div>

        <div className="flex flex-col items-center gap-4">
          <OTPInput
            onComplete={handleOTPComplete}
            disabled={isSubmitting || isResending}
            error={hasError}
          />
        </div>
      </div>

      {(isSubmitting || shouldShowSuccess) && (
        <AuthenticatingSection
          messageType={
            shouldShowSuccess &&
            (isExistingMember(status) || isNewMember(status))
              ? "success"
              : "authenticating"
          }
        />
      )}
      {shouldShowFooter && (
        <FooterSection
          status={status}
          isSubmitting={isSubmitting}
          isResending={isResending}
          onShowPasswordForm={onShowPasswordForm}
        />
      )}
    </div>
  );
};
